import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, NavigationEnd } from '@angular/router';
import { KeycloakService, KeycloakAuthGuard } from 'keycloak-angular';
import { Role } from 'lcmm-lib-js';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { SensorstationRole } from 'sensorstation-lib-js';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { EnvConfigurationService } from './env-config.service';
import { PrivacyRequestDialogComponent } from '../privacy-request-dialog/privacy-request-dialog.component';

export function authServiceInitializer(
  keycloak: KeycloakService,
  envService: EnvConfigurationService
): () => Promise<boolean> {
  return (): Promise<boolean> => {
    return envService.load().then(() => {
      let authUrl: string;
      if (window.location.hostname === 'localhost') {
        authUrl = envService.config.keycloakAuthUrl;
      } else {
        authUrl = `https://${window.location.hostname}/auth/`;
      }

      return keycloak.init({
        initOptions: {
          onLoad: 'login-required',
          checkLoginIframe: false,
        },
        config: {
          url: authUrl,
          clientId: envService.config.keycloakClientId,
          realm: envService.config.keycloakRealm,
        },
        enableBearerInterceptor: true,
        bearerExcludedUrls: [],
      });
    });
  };
}

const COOKIE_EXPIRE_IN_DAYS = 7;
const COOKIE_VALUE = 'lcmm-user';
const GROUP_PATTERN = environment.utilGroupPattern;

const ROLE_NO_DOWNLOAD = 'ROLE_NO_DOWNLOAD';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends KeycloakAuthGuard {
  private initialize: boolean;

  private userId: string;

  private admin: boolean;

  private dispatcher: boolean;

  private driver: boolean;

  private downloadDisabled: boolean;

  private noLoggedInPageSet: Set<string> = new Set<string>([
    '/app/contact',
    '/app/privacystatement',
    '/app/imprint',
    '/ui/contact',
    '/ui/privacystatement',
    '/ui/imprint',
  ]);

  private noLoggedInPage: boolean = null;

  private appPage: boolean = null;

  // SENSORSTATION BEGIN
  private sensorstationAdmin: boolean;

  private sensorstationViewer: boolean;

  private sensorstationEditor: boolean;

  // SENSORSTATION END

  private gdprRequested = false;

  constructor(
    protected router: Router,
    protected keycloakService: KeycloakService,
    private matDialog: MatDialog,
    private cookieService: CookieService,
    private snackBar: MatSnackBar,
    private ts: TranslateService,
    public envService: EnvConfigurationService
  ) {
    super(router, keycloakService);
    this.reset();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const { url } = this.router.routerState.snapshot;
        const callPara: string[] = url.split('/');
        this.noLoggedInPage = this.noLoggedInPageSet.has(url);
        this.appPage = callPara.length > 1 && callPara[1] === 'app';
      });
  }

  private reset(): void {
    this.userId = null;
    this.appPage = null;
    this.noLoggedInPage = null;
    this.admin = false;
    this.dispatcher = false;
    this.driver = false;
    this.downloadDisabled = false;
    this.initialize = true;
  }

  // SENSORSTATION BEGIN
  private resetSensorstationFields(): void {
    this.sensorstationAdmin = false;
    this.sensorstationEditor = false;
    this.sensorstationViewer = false;
  }
  // SENSORSTATION END

  private init(): void {
    if (this.initialize) {
      this.initialize = false;
      const myroles = this.keycloakService.getUserRoles();
      this.admin = this.hasRole([Role.Admin], myroles);
      this.dispatcher = this.hasRole([Role.Dispatcher], myroles);
      this.driver = this.hasRole([Role.Driver], myroles);
      this.downloadDisabled =
        this.hasRole([ROLE_NO_DOWNLOAD], myroles) &&
        !this.admin &&
        !this.dispatcher;

      // SENSORSTATION BEGIN
      this.sensorstationAdmin = this.hasRole(
        [SensorstationRole.SensorstationAdmin],
        myroles
      );
      this.sensorstationEditor = this.hasRole(
        [SensorstationRole.SensorstationEditor],
        myroles
      );
      this.sensorstationViewer = this.hasRole(
        [SensorstationRole.SensorstationViewer],
        myroles
      );
      // SENSORSTATION END
    }
  }

  // public functions

  public getUserId(): Promise<string> {
    this.init();
    return new Promise<string>((resolve, reject) => {
      if (this.userId) {
        resolve(this.userId);
      } else {
        this.loadUserProfile()
          .then((profile) => {
            this.userId = profile.id;
            resolve(this.userId);
          })
          .catch((err) => {
            this.userId = null;
            reject(err);
          });
      }
    });
  }

  public openGdprRequest(): void {
    this.init();
    this.loadUserProfile().then((profile) => {
      const cookieValue = this.cookieService.get(profile.username);
      const roles = this.keycloakService.getUserRoles();
      if (cookieValue !== COOKIE_VALUE) {
        const dialogRef = this.matDialog.open(PrivacyRequestDialogComponent, {
          maxHeight: '90vh',
          maxWidth: '90vw',
          // Check if vh/vw are ok with iOS Safari. Otherwise: maybe %
          disableClose: true,
          data: { roles },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result === true) {
            this.cookieService.set(
              profile.username,
              COOKIE_VALUE,
              COOKIE_EXPIRE_IN_DAYS
            );
          }
        });
      }
    });
  }

  public revokeGdpr(): void {
    this.init();
    this.loadUserProfile().then((profile) => {
      // Delete cookie
      this.cookieService.delete(profile.username);
      // Logout
      this.logout();
      this.reset();
    });
  }

  public isAccessAllowed(
    activatedRouteSnapshot: ActivatedRouteSnapshot
  ): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.checkForbiddenRoles()) {
        resolve(false);
        return;
      }

      if (!this.gdprRequested) {
        this.gdprRequested = true;
        this.openGdprRequest();
      }
      if (!this.authenticated) {
        this.keycloakAngular.login();
        resolve(false);
      }
      const requiredRoles = activatedRouteSnapshot.data.roles || [];
      const userHasRequiredRole = this.hasRole(requiredRoles, this.roles);

      if (!userHasRequiredRole) {
        this.redirectBasedOnRole().then(() =>
          this.showAccessDeniedMessage('GUARD.ACCESSDENIED')
        );
        resolve(false);
        return;
      }

      resolve(true);
    });
  }

  private showAccessDeniedMessage(messageKey: string): void {
    const message = this.ts.instant(messageKey);
    const action = this.ts.instant('GUARD.ACTION');
    this.snackBar.open(message, action, {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }

  private redirectBasedOnRole() {
    if (this.isSensorstationMember()) {
      return this.router.navigate(['/sensorstation']);
    }
    return this.router.navigate(['/']);
  }

  public checkForbiddenRoles(): boolean {
    return false;
  }

  public getGroupIdentifier(): string {
    this.init();
    let groupIdentifier: string = null;
    this.keycloakService.getUserRoles().some((element) => {
      if (element.startsWith(GROUP_PATTERN)) {
        groupIdentifier = element.replace(GROUP_PATTERN, '');
        return true;
      }
      return false;
    });
    return groupIdentifier;
  }

  public getUserRoles(): string[] {
    const userRoles = this.keycloakService.getUserRoles();
    return userRoles;
  }

  public hasRole(requiredRoles: string[], roles: string[]): boolean {
    this.init();
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }
    if (!roles || roles.length === 0) {
      return false;
    }
    return requiredRoles.some((role) => roles.includes(role));
  }

  public isAdmin(): boolean {
    this.init();
    return this.admin;
  }

  public isDispatcher(): boolean {
    this.init();
    return this.dispatcher;
  }

  public isDriver(): boolean {
    this.init();
    return this.driver;
  }

  public isDownloadEnabled(): boolean {
    this.init();
    return !this.downloadDisabled;
  }

  public isAdminOrDispatcher(): boolean {
    return this.isAdmin() || this.isDispatcher();
  }

  public logout(): Promise<void> {
    this.reset();
    // SENSORSTATION BEGIN
    if (this.isSensorstationMember()) {
      return this.keycloakService
        .logout(`${this.envService.config.grafanaUrl}logout`)
        .then(() => {
          this.resetSensorstationFields();
        });
    }
    // SENSORSTATION END
    return this.keycloakService.logout();
  }

  public loadUserProfile(): Promise<Keycloak.KeycloakProfile> {
    return this.keycloakService.loadUserProfile();
  }

  public getToken(): Promise<string> {
    return this.keycloakService.getToken();
  }

  public getUsername(): string {
    return this.keycloakService.getUsername();
  }

  public isLoggedInPage(): boolean {
    return !(this.noLoggedInPage !== null && this.noLoggedInPage);
  }

  public isNoLoggedInAppPage(): boolean {
    return this.appPage !== null && this.appPage;
  }

  // SENSORSTATION BEGIN
  public isSensorstationAdmin(): boolean {
    this.init();
    return this.sensorstationAdmin;
  }

  public isSensorstationEditor(): boolean {
    this.init();
    return this.sensorstationEditor;
  }

  public isSensorstationViewer(): boolean {
    this.init();
    return this.sensorstationViewer;
  }

  public isSensorstationMember(): boolean {
    if (this.isAdmin() || this.isDispatcher() || this.isDriver()) {
      return false;
    }
    return (
      this.isSensorstationAdmin() ||
      this.isSensorstationEditor() ||
      this.isSensorstationViewer()
    );
  }
  // SENSORSTATION END
}
